import { IContratoSocietario, StatusContratoSocietario } from '../../../hooks/useSocietario';
import { formatCNPJ } from '../../../utils/documento';

// Regras da listagem de contratos (/societario/contratos), sem React: o card e o
// cabecalho leem daqui.

// Abas por escopo (sessao 2026-05-08). "Minhas solicitacoes" = tudo que o usuario
// criou, em qualquer status (substitui a antiga /societario/historico).
export type AbaDaListagem = 'todos' | 'minhas' | 'finalizados' | 'arquivados';
export const ABAS_DA_LISTAGEM: AbaDaListagem[] = ['todos', 'minhas', 'finalizados', 'arquivados'];
export const ROTULOS_DAS_ABAS: Record<AbaDaListagem, string> = {
  todos: 'Todos',
  minhas: 'Minhas solicitações',
  finalizados: 'Finalizados',
  arquivados: 'Arquivados',
};

export type OrdemDaListagem = 'atualizados' | 'recentes' | 'antigos';
export const ORDEM_PADRAO: OrdemDaListagem = 'atualizados';
export const OPCOES_DE_ORDEM: { value: OrdemDaListagem; label: string }[] = [
  { value: 'atualizados', label: 'Atualizados recentemente' },
  { value: 'recentes', label: 'Criados mais recentes' },
  { value: 'antigos', label: 'Criados mais antigos' },
];

// "Inicial" e "Rascunho" sao o mesmo momento para o operador: um filtro so'.
export const STATUS_RASCUNHOS = 'rascunho_inicial,rascunho';

// Sem "Assinado", "Cancelado" e "Campos pendentes": nenhum fluxo grava esses status hoje
// (29/09, pedido do user). O card continua sabendo exibi-los, se aparecerem.
const OPCOES_DE_STATUS_ATIVOS = [
  { value: STATUS_RASCUNHOS, label: 'Rascunho' },
  { value: 'em_revisao', label: 'Em revisão' },
  { value: 'gerado', label: 'Gerado' },
];

/**
 * Status que fazem sentido em cada aba. Arquivados e Finalizados não têm filtro:
 * na primeira todos são arquivados; na segunda, sem o "Assinado", sobrava só "Gerado".
 */
export const opcoesDeStatusDaAba = (aba: AbaDaListagem) => {
  if (aba === 'arquivados' || aba === 'finalizados') return [];
  return OPCOES_DE_STATUS_ATIVOS;
};

/** Por que o filtro de status está desligado na aba (tooltip). */
export const motivoSemStatus: Partial<Record<AbaDaListagem, string>> = {
  arquivados: 'Na aba Arquivados todos já são arquivados',
  finalizados: 'Na aba Finalizados todos já foram gerados',
};

export const statusCabeNaAba = (status: string | null, aba: AbaDaListagem) =>
  !status || opcoesDeStatusDaAba(aba).some(o => o.value === status);

// Cor pelo SIGNIFICADO: ambar = precisa de acao; vermelho so' para cancelado.
export const SITUACOES: Record<StatusContratoSocietario, { rotulo: string; cor: string }> = {
  rascunho_inicial: { rotulo: 'Rascunho', cor: 'gray' },
  rascunho: { rotulo: 'Rascunho', cor: 'gray' },
  campos_pendentes: { rotulo: 'Campos pendentes', cor: 'orange' },
  em_revisao: { rotulo: 'Em revisão', cor: 'orange' },
  gerado: { rotulo: 'Gerado', cor: 'blue' },
  assinado: { rotulo: 'Assinado', cor: 'green' },
  cancelado: { rotulo: 'Cancelado', cor: 'red' },
  arquivado: { rotulo: 'Arquivado', cor: 'gray' },
};

const COM_DOCUMENTO: StatusContratoSocietario[] = ['gerado', 'em_revisao', 'assinado', 'arquivado'];
const RASCUNHOS: StatusContratoSocietario[] = ['rascunho_inicial', 'rascunho', 'campos_pendentes'];

export type AcaoPrincipal = 'continuar' | 'regerar' | 'revisar_vistoria' | 'baixar';

export interface EstadoDoCard {
  /** Alteracao v3 com documento cuja vistoria ninguem revisou: o back barra o download (409). */
  vistoriaPendente: boolean;
  podeBaixar: boolean;
  /** Reabrir no wizard: rascunhos e, no v3, gerado/em revisao (para regerar). */
  podeAbrirNoWizard: boolean;
  podeVerVistoria: boolean;
  acaoPrincipal: AcaoPrincipal | null;
  /** Precisa de alguem: pinta o card de ambar. */
  precisaDeAcao: boolean;
}

export function estadoDoCard(c: IContratoSocietario): EstadoDoCard {
  const alteracaoV3 = c.tipo_operacao === 'alteracao' && c.engine_usado === 'v3';
  const vistoriaPendente = alteracaoV3 && !!c.contrato_s3_key && !c.auditoria_revisada_em;
  const podeBaixar = COM_DOCUMENTO.includes(c.status) && !!c.contrato_s3_key && !vistoriaPendente;
  const rascunho = RASCUNHOS.includes(c.status);
  const podeAbrirNoWizard =
    rascunho || ((c.status === 'gerado' || c.status === 'em_revisao') && c.engine_usado === 'v3');
  // Vistoria compara o vigente com o documento gerado: sem documento, nao ha' o
  // que ver (rascunho arquivado, por exemplo).
  const podeVerVistoria = alteracaoV3 && COM_DOCUMENTO.includes(c.status) && !!c.contrato_s3_key;

  let acaoPrincipal: AcaoPrincipal | null = null;
  if (rascunho) acaoPrincipal = podeAbrirNoWizard ? 'continuar' : null;
  // Editado depois de gerar: o documento baixavel e' o antigo; o que falta e' regerar.
  else if (c.status === 'em_revisao' && podeAbrirNoWizard) acaoPrincipal = 'regerar';
  else if (vistoriaPendente && podeVerVistoria) acaoPrincipal = 'revisar_vistoria';
  else if (podeBaixar) acaoPrincipal = 'baixar';

  const precisaDeAcao =
    c.status === 'em_revisao' || c.status === 'campos_pendentes' || vistoriaPendente;
  return {
    vistoriaPendente,
    podeBaixar,
    podeAbrirNoWizard,
    podeVerVistoria,
    acaoPrincipal,
    precisaDeAcao,
  };
}

/**
 * Selo do card: a SITUACAO, nao o status cru. Gerado com a vistoria por revisar
 * aparece como "Vistoria pendente" (e' o que trava o download), num selo so'.
 */
export function situacaoDoCard(
  c: IContratoSocietario,
  estado: Pick<EstadoDoCard, 'vistoriaPendente'>,
): { rotulo: string; cor: string; solido: boolean } {
  if (estado.vistoriaPendente && c.status === 'gerado') {
    return { rotulo: 'Vistoria pendente', cor: 'orange', solido: true };
  }
  const s = SITUACOES[c.status] ?? { rotulo: c.status, cor: 'gray' };
  return { ...s, solido: false };
}

export const ROTULOS_DA_ACAO: Record<AcaoPrincipal, string> = {
  continuar: 'Continuar',
  regerar: 'Regerar',
  revisar_vistoria: 'Revisar vistoria',
  baixar: 'Baixar',
};

export const formatarDataHora = (valor?: string | null): string => {
  if (!valor) return '—';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return '—';
  return `${data.toLocaleDateString('pt-BR')} ${data.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const inicioDoDia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/** "agora", "há 5 min", "há 3 h" (mesmo dia), "ontem", "há 4 dias" (ate' 6) ou a data. */
export function tempoRelativo(valor?: string | null, agora: Date = new Date()): string {
  if (!valor) return '—';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return '—';
  const segundos = Math.floor((agora.getTime() - data.getTime()) / 1000);
  if (segundos < 60) return 'agora';
  if (segundos < 3600) return `há ${Math.floor(segundos / 60)} min`;
  const dias = Math.round((inicioDoDia(agora) - inicioDoDia(data)) / 86400000);
  if (dias === 0) return `há ${Math.floor(segundos / 3600)} h`;
  if (dias === 1) return 'ontem';
  if (dias < 7) return `há ${dias} dias`;
  return data.toLocaleDateString('pt-BR');
}

/**
 * Ultimo salvamento do contrato ou de um socio dele (os socios gravam em rotas
 * proprias e nao tocam o contrato). Mesma regra da ordem "Atualizados recentemente"
 * no back (`_ULTIMA_ATIVIDADE`).
 */
export function ultimaAtividade(c: IContratoSocietario): string | undefined {
  let maior = c.dt_atualizacao;
  let maiorMs = maior ? new Date(maior).getTime() : Number.NaN;
  (c.socios ?? []).forEach(s => {
    const ms = s.dt_atualizacao ? new Date(s.dt_atualizacao).getTime() : Number.NaN;
    if (!Number.isNaN(ms) && (Number.isNaN(maiorMs) || ms > maiorMs)) {
      maior = s.dt_atualizacao ?? undefined;
      maiorMs = ms;
    }
  });
  return maior;
}

// O back grava dt_criacao e dt_atualizacao no mesmo commit, com microssegundos de
// diferenca: abaixo de 2 s nao houve salvamento depois da criacao.
export const houveAtualizacao = (criacao?: string, atualizacao?: string): boolean => {
  if (!criacao || !atualizacao) return false;
  const tc = new Date(criacao).getTime();
  const ta = new Date(atualizacao).getTime();
  if (Number.isNaN(tc) || Number.isNaN(ta)) return false;
  return ta - tc > 2000;
};

/**
 * O banco guarda o CNPJ com e sem mascara: exibe sempre com. So' formata com 14
 * caracteres; o `formatCNPJ` completa com zeros e mostraria um CNPJ que nao existe.
 */
export const cnpjParaExibir = (cnpj: string): string =>
  cnpj.replace(/[^A-Za-z0-9]/g, '').length === 14 ? formatCNPJ(cnpj) : cnpj;
